import type { LiquidityScoreResponse } from '@/pages/api/liquidity-score';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetcherWithJwt } from '../utils/fetcher';
import { normalizeTokenAddress, tokenKey } from '../utils/normalizeAddress';

export const liquidityScoreQueryKey = (tokensParam: string | null) =>
  ['liquidity-score', tokensParam] as const;

/**
 * Fetches liquidity scores for a batch of tokens using the upstream batch API.
 * @param tokens - Array of { address, chainId } to fetch scores for.
 * @returns Normalized score data. Scores of -1 are mapped to undefined.
 */
export const useLiquidityScore = (
  tokens: Array<{ address: string; chainId: number }> | undefined,
) => {
  // Build a stable query key by sorting tokens deterministically
  const tokensParam = useMemo(() => {
    if (!tokens || tokens.length === 0) return null;

    const sorted = [...tokens].sort((a, b) => {
      const chainDiff = a.chainId - b.chainId;
      if (chainDiff !== 0) return chainDiff;
      return normalizeTokenAddress(a.address, a.chainId).localeCompare(
        normalizeTokenAddress(b.address, b.chainId),
      );
    });

    return sorted.map((t) => tokenKey(t.address, t.chainId)).join(',');
  }, [tokens]);

  const { data, error, isLoading, isFetching, refetch } = useQuery<LiquidityScoreResponse, Error>({
    queryKey: liquidityScoreQueryKey(tokensParam),
    queryFn: () => fetcherWithJwt(`/api/liquidity-score?tokens=${tokensParam}`),
    enabled: tokensParam !== null,
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnReconnect: false,
  });

  // Normalize: map -1 scores to undefined
  const normalizedData = useMemo(
    () =>
      data
        ? {
            ...data,
            data: data.data.map((item) => ({
              ...item,
              score: item.score === -1 ? undefined : item.score,
            })),
          }
        : undefined,
    [data],
  );

  return {
    data: normalizedData,
    error,
    isLoading,
    isFetching,
    refetch,
  };
};
